import { useState, useEffect } from "react";
import { useLocale } from "../../i18n/context";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Phone, MapPin, Menu, X } from "lucide-react";

export function Header() {
  const { t, localePath } = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      {/* Top utility bar */}
      <div className="bg-slate-800 text-white text-sm fixed top-0 w-full z-[1001]">
        <div className="max-w-[1200px] mx-auto px-5 py-2 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a
              href="tel:+37067193882"
              className="flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors no-underline"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>+370 671 93882</span>
            </a>
            <span className="hidden sm:flex items-center gap-1.5 text-slate-400">
              <MapPin className="w-3.5 h-3.5" />
              <span>Karklėnų g. 21, Vilnius</span>
            </span>
          </div>
          <LanguageSwitcher />
        </div>
      </div>

      {/* Main navigation */}
      <header
        className={`bg-white/95 backdrop-blur-sm fixed top-[36px] w-full z-[1000] transition-shadow duration-300 border-b border-slate-100 ${
          scrolled ? "shadow-md" : ""
        }`}
      >
        <nav
          aria-label="Main navigation"
          className="max-w-[1200px] mx-auto flex justify-between items-center px-5 h-[56px]"
        >
          {/* Brand */}
          <a
            href={localePath("/")}
            className="group no-underline max-w-[280px] leading-tight max-md:max-w-[200px]"
          >
            <span className="block text-base font-bold text-slate-900 group-hover:text-[#2196F3] transition-colors whitespace-nowrap max-md:text-sm">
              {t.common.brand}
            </span>
            <span className="block text-[11px] uppercase tracking-wide text-slate-500 whitespace-nowrap max-md:hidden">
              {t.common.brandTagline}
            </span>
          </a>

          {/* Desktop nav */}
          <ul className="list-none flex gap-1 items-center max-md:hidden">
            <li>
              <a
                href={localePath("/")}
                className="relative px-4 py-2 text-slate-600 font-medium text-sm transition-colors hover:text-[#2196F3] no-underline after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-[#2196F3] after:transition-all after:duration-300 hover:after:w-full"
              >
                {t.common.navHome}
              </a>
            </li>
            <li>
              <a
                href={localePath("/naudotos-padangos")}
                className="relative px-4 py-2 text-slate-600 font-medium text-sm transition-colors hover:text-[#2196F3] no-underline after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-[#2196F3] after:transition-all after:duration-300 hover:after:w-full"
              >
                {t.common.navUsedTires}
              </a>
            </li>
            <li>
              <a
                href={localePath("/padangu-montavimas-ir-remontas")}
                className="relative px-4 py-2 text-slate-600 font-medium text-sm transition-colors hover:text-[#2196F3] no-underline after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-[#2196F3] after:transition-all after:duration-300 hover:after:w-full"
              >
                {t.common.navMontavimas}
              </a>
            </li>
            <li>
              <a
                href={localePath("/ratlankiu-lyginimas")}
                className="relative px-4 py-2 text-slate-600 font-medium text-sm transition-colors hover:text-[#2196F3] no-underline after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-[#2196F3] after:transition-all after:duration-300 hover:after:w-full"
              >
                {t.common.navRatlankiai}
              </a>
            </li>
          </ul>

          {/* Mobile hamburger */}
          <button
            className="hidden max-md:flex items-center justify-center w-10 h-10 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </header>

      {/* Mobile overlay menu */}
      <div
        className={`fixed inset-x-0 bottom-0 z-[999] bg-white transition-all duration-300 md:hidden flex flex-col ${
          menuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        style={{ top: 92 }}
      >
        <nav className="flex flex-col items-center justify-center flex-1 gap-8">
          <a
            href={localePath("/")}
            onClick={() => setMenuOpen(false)}
            className="text-2xl font-semibold text-slate-800 hover:text-[#2196F3] transition-colors no-underline"
          >
            {t.common.navHome}
          </a>
          <a
            href={localePath("/naudotos-padangos")}
            onClick={() => setMenuOpen(false)}
            className="text-2xl font-semibold text-slate-800 hover:text-[#2196F3] transition-colors no-underline"
          >
            {t.common.navUsedTires}
          </a>
          <a
            href={localePath("/padangu-montavimas-ir-remontas")}
            onClick={() => setMenuOpen(false)}
            className="text-2xl font-semibold text-slate-800 hover:text-[#2196F3] transition-colors no-underline"
          >
            {t.common.navMontavimas}
          </a>
          <a
            href={localePath("/ratlankiu-lyginimas")}
            onClick={() => setMenuOpen(false)}
            className="text-2xl font-semibold text-slate-800 hover:text-[#2196F3] transition-colors no-underline"
          >
            {t.common.navRatlankiai}
          </a>
          <a
            href="tel:+37067193882"
            className="inline-flex items-center gap-2 bg-[#2196F3] text-white px-6 py-3 rounded-lg text-lg font-medium hover:bg-[#1976D2] transition-colors no-underline mt-4"
          >
            <Phone className="w-5 h-5" />
            +370 671 93882
          </a>
        </nav>
      </div>
    </>
  );
}
